import { useCallback, useEffect, useRef, useState } from 'react';
import notificationService from '../services/notificationService';
import auditService from '../services/auditService';
import { authService } from '../services/authService';
import { useModuleSettings } from '../contexts/ModuleSettingsContext';
import { formatAuditNotificationMessage } from '../utils/auditRuleCatalog';

const POLL_INTERVAL_MS = 30000;
const TOAST_SEEN_KEY = 'audit_notification_toast_seen_ids';

const AUDIT_TYPES = ['kardex_audit'];

function loadToastSeenIds() {
  try {
    const raw = sessionStorage.getItem(TOAST_SEEN_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function saveToastSeenIds(set) {
  try {
    sessionStorage.setItem(TOAST_SEEN_KEY, JSON.stringify([...set]));
  } catch {
    // ignore
  }
}

function filterAuditNotifications(items) {
  return (Array.isArray(items) ? items : []).filter((item) => AUDIT_TYPES.includes(item?.type));
}

export function useAuditNotifications() {
  const { kardexAuditModuleEnabled, loaded } = useModuleSettings();
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isRecipient, setIsRecipient] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const toastSeenIdsRef = useRef(loadToastSeenIds());
  const pollingRef = useRef(null);

  const syncAuditNotifications = useCallback((items) => {
    const auditItems = filterAuditNotifications(items);
    setNotifications(auditItems);
    setUnreadCount(auditItems.filter((item) => !item.read_at).length);
    return auditItems;
  }, []);

  const loadNotifications = useCallback(async () => {
    if (!authService.isAuthenticated() || !kardexAuditModuleEnabled) {
      setNotifications([]);
      setUnreadCount(0);
      return [];
    }

    setLoadingNotifications(true);
    try {
      const me = await auditService.amIRecipient();
      const recipient = Boolean(me?.data?.is_recipient);
      setIsRecipient(recipient);

      if (!recipient) {
        setNotifications([]);
        setUnreadCount(0);
        return [];
      }

      const response = await notificationService.list();
      return syncAuditNotifications(response?.data);
    } catch {
      return [];
    } finally {
      setLoadingNotifications(false);
    }
  }, [kardexAuditModuleEnabled, syncAuditNotifications]);

  const poll = useCallback(async () => {
    if (!authService.isAuthenticated() || !kardexAuditModuleEnabled) return;

    try {
      const auditItems = await loadNotifications();

      for (const item of auditItems) {
        if (!item?.id || item.read_at) continue;
        if (toastSeenIdsRef.current.has(item.id)) continue;

        toastSeenIdsRef.current.add(item.id);
        saveToastSeenIds(toastSeenIdsRef.current);

        setToast({
          isOpen: true,
          message: formatAuditNotificationMessage(item, 'Nueva alerta de auditoría'),
          type: item.toast_type || 'warning',
        });
        break;
      }
    } catch {
      // ignore polling errors
    }
  }, [kardexAuditModuleEnabled, loadNotifications]);

  const markNotificationRead = useCallback(async (id) => {
    await notificationService.markAsRead(id);
    await loadNotifications();
  }, [loadNotifications]);

  const markAllNotificationsRead = useCallback(async () => {
    const unread = notifications.filter((item) => !item.read_at);
    await Promise.all(unread.map((item) => notificationService.markAsRead(item.id)));
    await loadNotifications();
  }, [notifications, loadNotifications]);

  useEffect(() => {
    if (!loaded || !kardexAuditModuleEnabled) {
      setIsRecipient(false);
      setNotifications([]);
      setUnreadCount(0);
      return undefined;
    }

    poll();
    pollingRef.current = setInterval(poll, POLL_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loaded, kardexAuditModuleEnabled, poll]);

  return {
    toast,
    closeToast: () => setToast((t) => ({ ...t, isOpen: false })),
    unreadCount,
    isRecipient,
    notifications,
    loadingNotifications,
    refresh: poll,
    loadNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    kardexAuditModuleEnabled: loaded && kardexAuditModuleEnabled,
  };
}

export default useAuditNotifications;
